// screens/AudienceHome.tsx — Chunk AUD-W, plate 02: the Audience tab's
// home. Four tiles, the Posted list (per-platform receipt rows), the Doors
// list (status words, never a colour alone), and the inbox summary line.
// Empty states teach by shape — no instructional caption.
import { useEffect, useState } from 'preact/hooks';
import {
  ApiError,
  connectPublishAccount,
  syncPublishAccounts,
  type AudienceAccountApi,
  type AudienceSummaryApi,
} from '../lib/api';
import { getAudienceSummary } from '../lib/api';
import './AudienceHome.css';

export interface AudienceHomeProps {
  tenantId: string;
  onOpenReview: () => void;
  onOpenInbox: () => void;
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; summary: AudienceSummaryApi };

function clockLabel(iso: string): string {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function accountStatusLabel(status: string): string {
  switch (status) {
    case 'connected':
      return 'Connected';
    case 'audited_public':
      return 'Connected';
    case 'sandbox_private':
      return 'Sandbox';
    case 'revoked':
      return 'Disconnected';
    default:
      return status;
  }
}

export function AudienceHome({ tenantId, onOpenReview, onOpenInbox }: AudienceHomeProps) {
  const [load, setLoad] = useState<LoadState>({ phase: 'loading' });
  const [busyAccount, setBusyAccount] = useState<string | null>(null);
  const [accountNote, setAccountNote] = useState<string | null>(null);

  function readSummary(): void {
    setLoad({ phase: 'loading' });
    getAudienceSummary(tenantId)
      .then((summary) => setLoad({ phase: 'ready', summary }))
      .catch((err: unknown) => {
        setLoad({ phase: 'error', message: err instanceof ApiError ? err.message : 'the audience summary could not be read' });
      });
  }

  useEffect(() => {
    readSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  async function connect(platform: string): Promise<void> {
    setBusyAccount(platform);
    setAccountNote(null);
    try {
      const { url } = await connectPublishAccount(tenantId, platform);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      setAccountNote(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'could not start connecting that account');
    } finally {
      setBusyAccount(null);
    }
  }

  async function refresh(): Promise<void> {
    setBusyAccount('*refresh*');
    setAccountNote(null);
    try {
      const result = await syncPublishAccounts(tenantId);
      const skipped = result.skipped ?? [];
      setAccountNote(
        skipped.length > 0
          ? `Not carried yet, so not shown: ${skipped.map((a) => `${a.platform} ${a.handle}`).join(', ')}`
          : null
      );
      readSummary();
    } catch (err) {
      setAccountNote(err instanceof ApiError ? (err.serverMessage ?? err.message) : 'could not refresh the connected accounts');
    } finally {
      setBusyAccount(null);
    }
  }

  if (load.phase === 'loading') {
    return <div class="aud-home" aria-hidden="true" />;
  }
  if (load.phase === 'error') {
    return (
      <div class="aud-home">
        <div class="aud-home__say">
          The audience summary could not be read.
          <br />
          <span class="aud-home__say-soft">{load.message}</span>
        </div>
      </div>
    );
  }

  const { tiles, accounts, posted, waiting } = load.summary;

  return (
    <div class="aud-home">
      <div class="aud-home__tiles">
        <button type="button" class="aud-home__tile" onClick={onOpenReview} disabled={tiles.waitingForYes === 0}>
          <span class="aud-home__tile-n">{tiles.waitingForYes}</span>
          <span class="aud-home__tile-label">Waiting for your yes</span>
        </button>
        <div class="aud-home__tile aud-home__tile--static">
          <span class="aud-home__tile-n">{tiles.scheduled}</span>
          <span class="aud-home__tile-label">Scheduled</span>
        </div>
        <div class="aud-home__tile aud-home__tile--static">
          <span class="aud-home__tile-n">{tiles.postedThisWeek}</span>
          <span class="aud-home__tile-label">Posted this week</span>
        </div>
        <button type="button" class="aud-home__tile" onClick={onOpenInbox} disabled={tiles.unanswered === 0}>
          <span class="aud-home__tile-n">{tiles.unanswered}</span>
          <span class="aud-home__tile-label">Unanswered</span>
        </button>
      </div>

      {waiting.length > 0 && (
        <div class="aud-home__waiting">
          <div class="aud-home__eyebrow">Waiting for your yes</div>
          {waiting.map((w) => (
            <button type="button" class="aud-home__waiting-row" key={w.proposalId} onClick={onOpenReview}>
              {w.title}
            </button>
          ))}
        </div>
      )}

      <div class="aud-home__section">
        <div class="aud-home__eyebrow">Posted</div>
        {posted.length === 0 ? (
          <div class="aud-home__say-soft aud-home__empty">Nothing posted yet — what goes out lands here.</div>
        ) : (
          posted.map((p) => (
            <div class="aud-home__posted" key={p.key}>
              <div class="aud-home__posted-title">{p.title}</div>
              {p.platforms.map((pl) => (
                <div class="aud-home__receipt" key={pl.platform}>
                  {pl.platform} · {pl.status === 'published' && pl.publishedAt ? `live ${clockLabel(pl.publishedAt)}` : pl.status}
                  {pl.status === 'published' && pl.url ? (
                    <>
                      {' · '}
                      <a href={pl.url} target="_blank" rel="noreferrer">
                        open
                      </a>
                    </>
                  ) : null}
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div class="aud-home__section">
        <div class="aud-home__eyebrow">Doors</div>
        {accountNote ? <div class="aud-home__note">{accountNote}</div> : null}
        {accounts.length === 0 ? (
          <div class="aud-home__say-soft aud-home__empty">No accounts connected yet.</div>
        ) : (
          accounts.map((a: AudienceAccountApi) => (
            <div class="aud-home__door" key={a.id}>
              <span class="aud-home__door-handle">
                {a.platform} · @{a.handle}
              </span>
              <span class="aud-home__door-status">{accountStatusLabel(a.status)}</span>
            </div>
          ))
        )}
        <div class="aud-home__door-actions">
          <button type="button" class="aud-home__btn" disabled={busyAccount !== null} onClick={() => void connect('instagram')}>
            Connect
          </button>
          <button type="button" class="aud-home__btn" disabled={busyAccount !== null} onClick={() => void refresh()}>
            Refresh
          </button>
        </div>
      </div>

      <button type="button" class="aud-home__inbox-line" onClick={onOpenInbox}>
        {tiles.unanswered === 0 ? 'Nothing waiting in the inbox.' : `${tiles.unanswered} waiting for a reply in the inbox.`}
      </button>
    </div>
  );
}
